import { cn } from '../../utils';
import type { BulkAction } from './types';

export interface BulkActionsBarProps<T> {
  selectedCount: number;
  selectedRows: T[];
  actions: BulkAction<T>[];
  onClear: () => void;
  className?: string;
}

/**
 * Fixed-bottom bar that animates in when selectedCount > 0.
 * Renders count + actions + "clear selection".
 */
export function BulkActionsBar<T>({
  selectedCount,
  selectedRows,
  actions,
  onClear,
  className,
}: BulkActionsBarProps<T>) {
  const open = selectedCount > 0;
  return (
    <div
      role="toolbar"
      aria-label="bulk actions"
      aria-hidden={!open}
      className={cn(
        'pointer-events-none fixed bottom-4 left-1/2 -translate-x-1/2 z-30',
        'transition-all duration-150',
        open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3 px-3 py-2 rounded-[6px]',
          'bg-[var(--color-accent)] text-[var(--color-bg)] shadow-lg',
          'text-[13px]',
        )}
      >
        <span className="font-medium">{selectedCount} selected</span>
        <span className="h-4 w-px bg-[var(--color-bg)]/30" aria-hidden />
        {actions.map((a) => {
          const disabled = Boolean(a.disabledReason);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => !disabled && a.onClick(selectedRows)}
              disabled={disabled}
              title={a.disabledReason}
              className={cn(
                'inline-flex items-center gap-1 h-7 px-2 rounded-[4px]',
                'transition-colors',
                a.destructive
                  ? 'hover:bg-[var(--color-error)]/80'
                  : 'hover:bg-[var(--color-bg)]/10',
                'disabled:opacity-50 disabled:pointer-events-none',
              )}
            >
              {a.icon}
              <span>{a.label}</span>
            </button>
          );
        })}
        <span className="h-4 w-px bg-[var(--color-bg)]/30" aria-hidden />
        <button
          type="button"
          onClick={onClear}
          className="h-7 px-2 rounded-[4px] text-[12px] hover:bg-[var(--color-bg)]/10"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
